/* eslint-disable import/prefer-default-export */
import { FieldFunctionOptions, FieldPolicy, Reference } from '@apollo/client'
import { isEmpty } from 'lodash'

import {
  Filter,
  FilterMap,
  SortParams,
  getCacheKey,
  getFilteredData,
  getFilters,
  getNewIncomingData,
  getPaginatedData,
  getSortedData,
} from '../utils'

import { FilterDealsInput, DealObj, CrmOptions, DealsSortBy, SortOrder } from '~generated/types'

type IncomingDeals = {
  docs: Reference[]
  totalPages: number
  totalDocs: number
  page: number
}

type ExistingDeals = {
  [key: string]: IncomingDeals
}

export class PaginatedDealsQueryCache implements FieldPolicy {
  keyArgs: false

  merge = (
    existing: ExistingDeals,
    incoming: IncomingDeals,
    { readField, canRead, variables }: FieldFunctionOptions
  ) => {
    const cacheKey = getCacheKey({ ...variables?.filter, ...variables?.sort })
    const { docs: existingDocs = [], ...restExisting } = existing?.[cacheKey] || {}
    const { docs: incomingDocs = [], ...restIncoming } = incoming

    const newIncomingDocs = getNewIncomingData({ incomingData: incomingDocs, existingData: existingDocs, readField })
    const newExistingDocs = [...existingDocs.filter(canRead), ...newIncomingDocs]

    return {
      ...existing,
      [cacheKey]: {
        ...restExisting,
        ...restIncoming,
        docs: newExistingDocs,
      },
    }
  }

  read = (existing: ExistingDeals, options: FieldFunctionOptions) => {
    if (!existing) return undefined

    const { variables, readField } = options
    const { pagination, filter, sort } = variables

    const filters = getFilters(filter, this.filters)
    const cacheKey = getCacheKey({ ...filter, ...sort })
    const cachedData = existing[cacheKey]

    if (!cachedData) return undefined

    const filteredDocs = getFilteredData({
      data: cachedData.docs,
      filter,
      filters,
      readField,
    })

    const paginatedDocs = getPaginatedData({
      filteredData: filteredDocs,
      page: cachedData?.page,
      limit: pagination?.limit,
    })

    const sortedDocs = getSortedData({
      data: paginatedDocs,
      sortKey: sort?.sortBy || DealsSortBy.NAME,
      sortOrder: sort?.sort || SortOrder.ASC,
      sortings: this.sort,
      readField,
    })

    return { ...cachedData, docs: sortedDocs }
  }

  /**
   * Sort
   */

  get sort() {
    return {
      [DealsSortBy.NAME]: this.sortByName,
      [DealsSortBy.REVENUE]: this.sortByRevenue,
      [DealsSortBy.DATE]: this.sortByDate,
    }
  }

  sortByName = ({ data, order, readField }: SortParams) => {
    return [...data].sort((docA, docB) => {
      const nameA = readField<string>('name', docA)
      const nameB = readField<string>('name', docB)
      if (!nameA) return order === SortOrder.ASC ? 1 : -1
      if (!nameB) return order === SortOrder.ASC ? -1 : 1

      return order === SortOrder.ASC ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA)
    })
  }

  sortByRevenue = ({ data, order, readField }: SortParams) => {
    return [...data].sort((docA, docB) => {
      const amountA = Number.parseInt(readField<string>('amount', docA), 10) || 0
      const amountB = Number.parseInt(readField<string>('amount', docB), 10) || 0
      return order === SortOrder.ASC ? amountA - amountB : amountB - amountA
    })
  }

  sortByDate = ({ data, order, readField }: SortParams) => {
    return [...data].sort((docA, docB) => {
      const closeDateA = readField<number>('closeDate', docA)
      const closeDateB = readField<number>('closeDate', docB)
      const timeA = closeDateA ? new Date(closeDateA).getTime() : 0
      const timeB = closeDateB ? new Date(closeDateB).getTime() : 0
      return order === SortOrder.ASC ? timeA - timeB : timeB - timeA
    })
  }

  /**
   * Filters
   */

  get filters() {
    return {
      ids: this.ids,
      query: this.query,
      source: this.source,
      stage: this.stage,
      ownerIds: this.ownerIds,
      companiesIds: this.companiesIds,
      startCloseDate: this.startCloseDate,
      endCloseDate: this.endCloseDate,
    } as FilterMap<FilterDealsInput>
  }

  ids: Filter<FilterDealsInput> = (ref, filter, readField) => {
    const key: keyof DealObj = 'id'
    const dealId = readField(key, ref) as string
    return isEmpty(filter.ids) || filter.ids?.includes(dealId)
  }

  source: Filter<FilterDealsInput> = (ref, filter, readField) => {
    if (isEmpty(filter.source)) return true

    const key: keyof DealObj = 'crm'
    const dealCrm = readField(key, ref) as CrmOptions
    return dealCrm && filter.source.includes(dealCrm)
  }

  stage: Filter<FilterDealsInput> = (ref, filter, readField) => {
    const key: keyof DealObj = 'stage'
    const dealStage = readField(key, ref) as string
    return isEmpty(filter.stages) || filter.stages?.includes(dealStage)
  }

  ownerIds: Filter<FilterDealsInput> = (ref, filter, readField) => {
    const key: keyof DealObj = 'owner'
    const dealOwnerRef = readField(key, ref) as Reference

    // If filter is empty return all
    if (isEmpty(filter.ownersIds)) {
      return true
    }

    // If filter is not empty and owner id is in filter
    if (dealOwnerRef) {
      const dealOwnerId = readField('id', dealOwnerRef) as string
      return filter.ownersIds.includes(dealOwnerId)
    }

    // If filter is not empty and owner absent
    return false
  }

  companiesIds: Filter<FilterDealsInput> = (ref, filter, readField) => {
    const key: keyof DealObj = 'company'
    const companyRef = readField(key, ref) as Reference

    // If filter is empty return all
    if (isEmpty(filter.companiesIds)) {
      return true
    }

    // If filter is not empty and company id is in filter
    if (companyRef) {
      const companyId = readField('id', companyRef) as string
      return filter.companiesIds.includes(companyId)
    }

    // If filter is not empty and owner absent
    return false
  }

  query: Filter<FilterDealsInput> = (ref, filter, readField) => {
    const nameKey: keyof DealObj = 'name'
    const name = readField(nameKey, ref) as string
    const searchRegExp = new RegExp(filter.query, 'i')
    return searchRegExp.test(name)
  }

  startCloseDate: Filter<FilterDealsInput> = (ref, filter, readField) => {
    if (!filter.startCloseDate) return true

    const key: keyof DealObj = 'closeDate'
    const closeDate = readField(key, ref) as string
    const closeDateTime = new Date(closeDate).getTime()
    const startCloseDateTime = new Date(filter.startCloseDate).getTime()
    return closeDateTime >= startCloseDateTime
  }

  endCloseDate: Filter<FilterDealsInput> = (ref, filter, readField) => {
    if (!filter.endCloseDate) return true

    const key: keyof DealObj = 'closeDate'
    const closeDate = readField(key, ref) as string
    const closeDateTime = new Date(closeDate).getTime()
    const endCloseDateTime = new Date(filter.endCloseDate).getTime()
    return closeDateTime <= endCloseDateTime
  }
}
